import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Swords, Clock, HelpCircle } from "lucide-react";
import Reveal from "../components/ui/Reveal";
import IconBox from "../components/ui/IconBox";
import SEO from "../components/seo/SEO";
import { APP_STORE_URL, PLAY_STORE_URL, challengeStoreClick } from "../constants/appStores";
import { getPublicChallenge } from "../lib/challenges";

const CLOSED_COPY = {
  expired: "This challenge has expired. Ask your friend to send you a new one.",
  taken: "Someone has already joined this challenge. Ask your friend for a new link.",
  completed: "This challenge is already finished.",
  declined: "This challenge was declined.",
};

export default function ChallengeLinkPage() {
  const { challengeCode } = useParams();
  const code = (challengeCode || "").toUpperCase();
  const [challenge, setChallenge] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | missing | error

  useEffect(() => {
    let active = true;
    setStatus("loading");

    getPublicChallenge(code)
      .then((data) => {
        if (!active) return;
        setChallenge(data);
        setStatus("ready");
      })
      .catch((error) => {
        if (active) setStatus(error.status === 404 ? "missing" : "error");
      });

    return () => {
      active = false;
    };
  }, [code]);

  const open = status === "ready" && challenge?.state === "open";
  const closedMessage = status === "ready" ? CLOSED_COPY[challenge?.state] : null;
  const name = challenge?.challenger_first_name;

  let heading = "You've been challenged";
  if (status === "loading") heading = "Loading challenge…";
  else if (status === "missing") heading = "Challenge not found";
  else if (status === "ready" && !open) heading = "This challenge is closed";
  else if (open && name) heading = `${name} challenged you`;

  const openInApp = () => {
    window.location.href = `campusgenie://c/${code}`;
  };

  return (
    <>
      <SEO
        title="Join a challenge"
        description="You've been challenged on CampusGenie. Open the app to accept."
        url={`/c/${code}`}
        noindex
      />

      <section className="section" style={{ paddingTop: "calc(var(--header-height) + var(--space-12))" }}>
        <div className="container">
          <div className="grid grid-2" style={{ alignItems: "center", gap: "var(--space-12)" }}>
            <Reveal>
              <div style={{ marginBottom: "var(--space-4)" }}>
                <IconBox icon={Swords} size={52} />
              </div>
              <span className="eyebrow">Challenge a friend</span>
              <h1 className="heading-1" style={{ marginBottom: "var(--space-4)" }}>
                {heading}
              </h1>

              {open && (
                <>
                  <p className="body-large" style={{ marginBottom: "var(--space-4)" }}>
                    Same test, one winner. Accept in the app and you both answer the same questions.
                  </p>
                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 var(--space-6)", display: "grid", gap: "var(--space-2)" }}>
                    <li><strong>{challenge.subject}</strong></li>
                    <li style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <HelpCircle size={16} aria-hidden /> {challenge.total_questions} questions
                    </li>
                    <li style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Clock size={16} aria-hidden /> {challenge.minutes} minutes
                    </li>
                  </ul>
                  <p className="body-small" style={{ marginBottom: "var(--space-6)" }}>
                    Challenge code: <code className="legal-prose-code">{code}</code>
                  </p>
                  <button type="button" className="btn btn-primary btn-lg" onClick={openInApp}>
                    Accept in the app
                  </button>
                </>
              )}

              {closedMessage && <p className="body-large">{closedMessage}</p>}
              {status === "missing" && (
                <p className="body-large">
                  We couldn&apos;t find a challenge with the code <code className="legal-prose-code">{code}</code>. Check the link
                  and try again.
                </p>
              )}
              {status === "error" && (
                <>
                  <p className="body-large" style={{ marginBottom: "var(--space-4)" }}>
                    We couldn&apos;t load the challenge details, but you can still accept in the app with the code{" "}
                    <code className="legal-prose-code">{code}</code>.
                  </p>
                  <button type="button" className="btn btn-primary btn-lg" onClick={openInApp}>
                    Open in the app
                  </button>
                </>
              )}
            </Reveal>

            <Reveal delay={0.08}>
              <div className="contact-form-card" style={{ textAlign: "center" }}>
                <h3 className="heading-4" style={{ marginBottom: "var(--space-2)" }}>
                  {open ? "Don't have CampusGenie yet?" : "Get CampusGenie"}
                </h3>
                <p className="body-small" style={{ marginBottom: "var(--space-6)" }}>
                  {open
                    ? "Install it, finish signing up, and the challenge will be waiting for you."
                    : "Free on iOS and Android. Challenge your own friends, keep a daily streak and practise JAMB."}
                </p>
                <div className="app-store-buttons" style={{ justifyContent: "center" }}>
                  <a href={APP_STORE_URL} onClick={challengeStoreClick(code)} className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                    Download on iOS
                  </a>
                  <a href={PLAY_STORE_URL} onClick={challengeStoreClick(code)} className="btn btn-secondary" target="_blank" rel="noopener noreferrer">
                    Get it on Android
                  </a>
                </div>
                <p className="body-small" style={{ marginTop: "var(--space-6)" }}>
                  <Link to="/">Learn more about CampusGenie</Link>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
